// Wraps `Auth0Provider` with React Router so `loginWithRedirect` returns the
// user back to the page they came from. Also forwards Auth0 state into the
// Zustand store and exposes the access-token getter to the axios interceptor.
//
// Allowlist enforcement is server-side. After Auth0 login this component
// probes `GET /me/profile`; the backend's email-allowlist enforcer (which
// reads UNION of `AUTH0_ALLOWED_EMAILS` Fly secret + the dynamic
// `user_allowlist` table) returns 200 for allowed emails and 403 otherwise.
// No client-side allowlist baked in — admins invite via /admin/users and
// the change takes effect immediately, no SPA redeploy required.

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ReactNode, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/hooks/useAuthStore";
import { setAuthTokenProvider } from "@/services/api/client";
import { fetchProfile } from "@/services/api/me";

interface Props {
  children: ReactNode;
}

const DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
const CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";
const AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE ?? "";
const ENFORCE = (import.meta.env.VITE_AUTH0_ENFORCE ?? "true") !== "false";

function AuthBridge({ children }: Props) {
  const {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const setLoading = useAuthStore((s) => s.setLoading);

  // Install the token provider synchronously during render — useEffect runs
  // AFTER children mount, which means the first API call from any child
  // would fire before the provider was set and bounce with 401. useMemo
  // runs before children render, so descendants always see the latest
  // provider.
  //
  // When ``getAccessTokenSilently`` fails (Auth0 SDK errors like
  // ``login_required`` / ``invalid_grant`` / refresh-token expiry) we
  // proactively bounce to the universal-login page so the user doesn't
  // sit on a screen that silently 401s every API call. The api/client
  // interceptor has a backstop for the rare case where this redirect
  // doesn't fire.
  useMemo(() => {
    setAuthTokenProvider(async () => {
      if (!ENFORCE) return null;
      try {
        return await getAccessTokenSilently();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const isRecoverableAuthError = /login_required|invalid_grant|consent_required|missing_refresh_token/i.test(
          message,
        );
        if (isRecoverableAuthError) {
          loginWithRedirect({
            appState: { returnTo: location.pathname + location.search },
          }).catch(() => {
            // Redirect could itself fail (network etc.) — fall through
            // to the response interceptor's stale-auth recovery.
          });
        }
        return null;
      }
    });
  }, [getAccessTokenSilently, loginWithRedirect, location.pathname, location.search]);

  useEffect(() => {
    setLoading(isLoading);
    if (!isLoading) {
      setSession(
        user
          ? {
              sub: user.sub ?? "unknown",
              email: user.email,
              name: user.name,
              picture: user.picture,
            }
          : null,
        isAuthenticated,
      );
    }
  }, [isLoading, user, isAuthenticated, setSession, setLoading]);

  // Force the Auth0 redirect immediately if the user isn't logged in.
  // We never let the chat shell render for anonymous visitors — the
  // Auth0 universal-login domain is the first thing they see.
  // Skip the /callback route, which has to render to finish the redirect
  // round-trip.
  useEffect(() => {
    if (!ENFORCE) return;
    if (isLoading) return;
    if (isAuthenticated) return;
    if (location.pathname === "/callback") return;
    loginWithRedirect({
      appState: { returnTo: location.pathname + location.search },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect, location]);

  // Hold rendering until the Auth0 SDK has finished hydrating from
  // localStorage. Otherwise the first API calls fire before the token
  // provider is ready and bounce with 401.
  if (ENFORCE && isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment-100 font-serif italic text-warmgray-400">
        Loading…
      </div>
    );
  }

  // Anonymous on a non-callback route → redirect already in flight; show a
  // minimal placeholder rather than the chat skeleton.
  if (ENFORCE && !isAuthenticated && location.pathname !== "/callback") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment-100 font-serif italic text-warmgray-400">
        Redirecting to sign-in…
      </div>
    );
  }

  // Server-side allowlist probe. Hits /me/profile after Auth0 confirms the
  // user. The backend's `_enforce_email_allowlist` (reading env-list UNION
  // user_allowlist table) gates the request — 200 means allowed, 403 means
  // not. Any other error is treated as transient and shows a retry path.
  return (
    <AllowlistGate user={user} onLogout={logout}>
      {children}
    </AllowlistGate>
  );
}


function AllowlistGate({
  user,
  onLogout,
  children,
}: {
  user: ReturnType<typeof useAuth0>["user"];
  onLogout: ReturnType<typeof useAuth0>["logout"];
  children: ReactNode;
}) {
  // Probe runs only when enforcement is on. In bypass mode (dev/test) the
  // SPA always renders the app.
  const probe = useQuery({
    queryKey: ["me", "profile", "allowlist-probe"],
    queryFn: fetchProfile,
    enabled: ENFORCE,
    retry: false,
    staleTime: Infinity,
  });

  if (!ENFORCE) return <>{children}</>;

  if (probe.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment-100 font-serif italic text-warmgray-400">
        Verifying access…
      </div>
    );
  }

  if (probe.isError) {
    const status = isAxiosError(probe.error)
      ? probe.error.response?.status
      : undefined;
    if (status === 403) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-parchment-100 px-6">
          <div className="max-w-md text-center">
            <span className="smallcaps text-oxblood-500">Access denied</span>
            <h2 className="mt-3 font-display text-3xl text-espresso-700">
              This Eryndal tenant is private.
            </h2>
            <p className="mx-auto mt-4 font-serif text-warmgray-500">
              Signed in as <span className="font-mono">{user?.email ?? "unknown"}</span>.
              That address isn't on the allowlist for this deployment.
            </p>
            <button
              type="button"
              onClick={() =>
                onLogout({ logoutParams: { returnTo: window.location.origin } })
              }
              className="mt-8 inline-flex items-center justify-center border border-oxblood-500 bg-oxblood-500 px-6 py-2 font-serif text-parchment-50 hover:bg-oxblood-600"
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }
    // 401 means the token rotated mid-probe — let the existing redirect
    // logic in AuthBridge handle it on the next render. Anything else is
    // a transient error: show a one-line retry.
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment-100 px-6">
        <div className="max-w-md text-center">
          <span className="smallcaps text-oxblood-500">Couldn't verify access</span>
          <p className="mx-auto mt-4 font-serif text-warmgray-500">
            The backend didn't respond. Refresh to retry, or sign out if this
            keeps happening.
          </p>
          <button
            type="button"
            onClick={() => probe.refetch()}
            className="mt-6 inline-flex items-center justify-center border border-oxblood-500 bg-oxblood-500 px-6 py-2 font-serif text-parchment-50 hover:bg-oxblood-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function Auth0ProviderWithHistory({ children }: Props) {
  const navigate = useNavigate();

  // Always wrap with Auth0Provider so consumer hooks (useAuth0) never throw,
  // even in bypass mode. ProtectedRoute / Login decide whether to actually
  // trigger loginWithRedirect based on VITE_AUTH0_ENFORCE.
  const domain = DOMAIN || "placeholder.eu.auth0.com";
  const clientId = CLIENT_ID || "placeholder";

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: AUDIENCE || undefined,
      }}
      onRedirectCallback={(state) => {
        navigate(state?.returnTo || "/chat", { replace: true });
      }}
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <AuthBridge>{children}</AuthBridge>
    </Auth0Provider>
  );
}
